import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import * as controlsActions from '../actions/controls';
import { usePlayer } from '../PlayerContext';
import { useOptions } from '../OptionsContext';
import { CHROMECAST_STATES } from './utils';
import ControlBar from './ControlBar/ControlBar';
import NextVideo from './ControlBar/NextVideo/NextVideo';
import MiniControl from './MiniControl/MiniControl';
import ErrorMessage from './ErrorMessage/ErrorMessage';
import MetadataOverlay from './Overlays/MetadataOverlay';
import ChromecastOverlay from './Overlays/ChromecastOverlay';
import ChromecastOnboardingOverlay from './Overlays/ChromecastOnboardingOverlay';
import PauseOverlay from './Overlays/PauseOverlay';
import SeekOverlay from './Overlays/SeekOverlay';
import ParentalControlOverlay from './Overlays/ParentalControlOverlay';
import {
  PlayerBackground,
  ChromecastBackground,
} from './Player/styles';

import {
  PlayerSpinner,
} from './common';
import ChromecastButton from './ControlBar/Chromecast/ChromecastButton';
import AdInfo from './ControlBar/AdInfo/AdInfo';
import AdControl from './ControlBar/AdInfo/AdControl';
import MiniAdControl from './MiniControl/AdControl/AdControl';
import { selectNormalizedCurrentTime } from '../selectors';
import platform, { OS_TYPES } from '../../player/utils/platform';

function Player() {
  const {
    controlsHideDelay,
    mini,
    seekStepInSeconds,
    hide,
  } = useOptions();

  const player = usePlayer();
  const dispatch = useDispatch();

  const {
    isFullscreen,
    error,
    viewableMetadata,
    metadata,
    playing,
    rights,
    isLive,
    isInitialized,
    chromecastState,
    isBuffering,
    chromecastEnabled,
    isPlayingAds,
    isPinCodeRequired,
    isAirPlaying,
    controlsVisible,
    menuVisible,
  } = useSelector(({ playback, controls }) => ({
    isFullscreen: playback.isFullscreen,
    error: playback.error,
    viewableMetadata: playback.viewableMetadata,
    metadata: playback.metadata,
    playing: playback.playing,
    rights: playback.rights,
    isLive: playback.isLive,
    isInitialized: playback.isInitialized,
    chromecastState: playback.chromecastState,
    isBuffering: playback.isBuffering,
    chromecastEnabled: playback.chromecastEnabled,
    isPlayingAds: playback.isPlayingAds,
    isPinCodeRequired: playback.isPinCodeRequired,
    isAirPlaying: playback.isAirPlaying,
    controlsVisible: controls.controlsVisible,
    menuVisible: controls.menuVisible,
  }), shallowEqual);
  const rating = hide.rating ? null : viewableMetadata.contentRating;

  const [controlBarHasMouseover, setControlBarHasMouseover] = useState(false);
  const [forceShowControlBar, setForceShowControlBar] = useState(false);
  const mouseMoveTimeout = useRef(null);
  const controlBarRef = useRef(null);

  const isMobile = [OS_TYPES.IOS, OS_TYPES.ANDROID].includes(platform.os);

  // Use ref to get proper state in events
  const onMouseLeave = useRef();
  onMouseLeave.current = () => {
    if (forceShowControlBar) {
      return;
    }

    clearTimeout(mouseMoveTimeout.current);
    mouseMoveTimeout.current = 0;
    dispatch(controlsActions.show(!playing, mini, player));
  };
  // Prevents hundreds bind/unbind event
  const onMouseLeaveHandler = useCallback(() => {
    if (isMobile) return; // no need to trigger if is mobile as there is no mouse
    onMouseLeave.current();
  }, []);

  const checkControlBarHasMouseOver = (target) => {
    if (controlBarHasMouseover && controlBarRef.current) {
      const isMouseOver = controlBarRef.current.contains(target);

      if (!isMouseOver) {
        setControlBarHasMouseover(false);
      }
    }
  };

  // fix wrong state in timeout
  const toggleControls = useRef();
  toggleControls.current = () => {
    clearTimeout(mouseMoveTimeout.current);
    mouseMoveTimeout.current = 0;
    if (forceShowControlBar || controlBarHasMouseover) {
      return;
    }

    dispatch(controlsActions.show(!playing, mini, player));
  };

  const onMouseMove = useRef();
  onMouseMove.current = (e) => {
    if (!controlsVisible) {
      dispatch(controlsActions.show(true, mini, player));
    }

    clearTimeout(mouseMoveTimeout.current);

    checkControlBarHasMouseOver(e.target);

    mouseMoveTimeout.current = setTimeout(() => toggleControls.current(), controlsHideDelay);
  };
  const onMouseMoveHandler = useCallback(e => onMouseMove.current(e), []);

  const maxRewindBySeconds = useSelector(selectNormalizedCurrentTime);

  const enableForceShowControlBar = () => {
    setForceShowControlBar(true);
  };

  const disableForceShowControlBar = () => {
    setForceShowControlBar(false);
    dispatch(controlsActions.show(!playing, mini, player));
  };

  const togglePlay = () => {
    const newPlayingValue = !playing;
    dispatch(controlsActions.show(!newPlayingValue, mini, player));
    player.togglePlay();

    clearTimeout(mouseMoveTimeout.current);
    mouseMoveTimeout.current = setTimeout(() => {
      if (!newPlayingValue || forceShowControlBar || controlBarHasMouseover) {
        return;
      }
      dispatch(controlsActions.show(false, mini, player));
      mouseMoveTimeout.current = 0;
    }, controlsHideDelay);
  };

  const handleKeyPress = (e) => {
    switch (e.key) {
      case ' ': {
        e.preventDefault();
        togglePlay();
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();

        player.rewind(Math.min(maxRewindBySeconds, seekStepInSeconds));
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        player.fastForward(seekStepInSeconds);
        break;
      }

      default: {
        break;
      }
    }
  };

  useEffect(() => () => {
    clearTimeout(mouseMoveTimeout.current);
    mouseMoveTimeout.current = 0;
  }, []);

  useEffect(() => {
    if (playing && !mouseMoveTimeout.current) {
      toggleControls.current();
    }
  }, [isInitialized]);

  useEffect(() => {
    // MDM-13509 - force hiding control bar on going fullscreen in Safari
    if (isFullscreen) {
      setControlBarHasMouseover(false);
      dispatch(controlsActions.show(!playing, mini, player));
    }
  }, [isFullscreen]);

  useEffect(() => {
    dispatch(controlsActions.show(!playing, mini, player));
  }, [mini]);

  const isChromecasting = chromecastEnabled && chromecastState === CHROMECAST_STATES.CONNECTED;

  const showSpinner = isBuffering
        && !isChromecasting
        && !mini
        && !isPinCodeRequired
        && !isPlayingAds
        && !error;

  // The pause overlay only applies to live streams at the moment and is ignored for all other types of assets.
  const showPauseOverlay = !playing
        && isLive
        && !rights.pause
        && !showSpinner
        && !isPlayingAds
        && isInitialized
        && !(mini && controlsVisible); // show pause overlay in miniplayer only if controls are hidden

  const showMetadataOverlay = !error && !mini && (!hide.hideBackArrow || !hide.hideTitle);

  const parentalControl = isPinCodeRequired ? (
    <ParentalControlOverlay mini={mini} />
  ) : null;

  const banner = metadata?.banner || viewableMetadata?.banner;

  const containerRef = useRef(null);

  useEffect(() => {
    containerRef.current?.focus();
  }, [containerRef.current, playing]);

  const showNextVideoComponent = !error && !isPlayingAds && !mini
    && player.model.nextVideo && !isChromecasting && !isAirPlaying;

  return (
    <div
      ref={containerRef}
      tabIndex="0"
      onKeyDown={handleKeyPress}
      onMouseLeave={onMouseLeaveHandler}
      onMouseMove={onMouseMoveHandler}
      onTouchMove={onMouseMoveHandler}
      onMouseEnter={onMouseMoveHandler}
    >
      {showSpinner && <PlayerSpinner />}

      {(showMetadataOverlay || (isChromecasting && !mini)) && (
        <MetadataOverlay
          episode={viewableMetadata.episode}
          title={viewableMetadata.episode?.title || viewableMetadata.title}
          hidden={!controlsVisible}
          hideBackArrow={hide.hideBackArrow}
          hideTitle={isPlayingAds || !isInitialized ? true : hide.hideTitle}
          isBuffering={isBuffering}
          rating={rating}
        />
      )}

      {((!isPlayingAds && chromecastEnabled && !mini && controlsVisible) || (isChromecasting && !mini))
        && !hide.cast && <ChromecastButton isPlayingAds={isPlayingAds} />}

      {isPlayingAds
        && <AdInfo mini={mini} />}

      <PlayerBackground
        hideCursor={!controlsVisible}
        onClick={togglePlay}
      />

      {isChromecasting && (
        <ChromecastBackground banner={banner} />
      )}

      {isChromecasting && !mini && (
        <ChromecastOverlay />
      )}

      {chromecastEnabled && !mini && (
        <ChromecastOnboardingOverlay
          chromecastState={chromecastState}
          controlBarHasMouseover={controlBarHasMouseover}
          onShow={enableForceShowControlBar}
          onHide={disableForceShowControlBar}
        />
      )}

      {showPauseOverlay && (
        <PauseOverlay />
      )}

      {!mini && (
        <SeekOverlay />
      )}

      {error && !parentalControl && <ErrorMessage error={error} miniPlayer={mini} />}

      {showNextVideoComponent && (
        <NextVideo key={viewableMetadata.id} hide={menuVisible} settings={player.model.nextVideo} />
      )}
      {!error && isPlayingAds && (
        mini ? (
          <MiniAdControl />
        ) : (
          <AdControl />
        )
      )}

      {!error && !isPinCodeRequired && (isInitialized || isPlayingAds) && (
        mini ? (
          <MiniControl
            ref={controlBarRef}
            onHoverStateChange={setControlBarHasMouseover}
            isPauseOverlayVisible={showPauseOverlay}
          />
        ) : (
          <ControlBar
            ref={controlBarRef}
            onHoverStateChange={setControlBarHasMouseover}
          />
        )
      )}
      {parentalControl}
    </div>
  );
}

export default React.memo(Player);
